/**
 * InfographicModal - Displays a stored or freshly-generated position/role infographic.
 * Supports: view existing, generate on demand, regenerate, download PNG.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getPositionInfographic, generatePositionInfographic } from '../../services/teamApi';

interface InfographicModalProps {
  teamId: number;
  positionId: number;
  roleName: string;
  existingInfographic?: { base64: string; mimeType: string } | null;
  onClose: () => void;
  onGenerated?: () => void;
}

export const InfographicModal: React.FC<InfographicModalProps> = ({
  teamId,
  positionId,
  roleName,
  existingInfographic,
  onClose,
  onGenerated,
}) => {
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const setImageFromResult = useCallback((base64: string, mimeType: string) => {
    setImageDataUrl(`data:${mimeType};base64,${base64}`);
  }, []);

  const loadInfographic = useCallback(async () => {
    if (existingInfographic) {
      setImageFromResult(existingInfographic.base64, existingInfographic.mimeType);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const stored = await getPositionInfographic(teamId, positionId);
      if (stored) {
        setImageFromResult(stored.image_base64, stored.mime_type);
        setLoading(false);
        return;
      }
      // No stored infographic -- auto-generate
      const result = await generatePositionInfographic(teamId, positionId);
      setImageFromResult(result.image_base64, result.mime_type);
      onGenerated?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load infographic');
    } finally {
      setLoading(false);
    }
  }, [teamId, positionId, existingInfographic, setImageFromResult, onGenerated]);

  const handleRegenerate = useCallback(async () => {
    setGenerating(true);
    setError('');
    try {
      const result = await generatePositionInfographic(teamId, positionId);
      setImageFromResult(result.image_base64, result.mime_type);
      onGenerated?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate infographic');
    } finally {
      setGenerating(false);
    }
  }, [teamId, positionId, setImageFromResult, onGenerated]);

  useEffect(() => {
    loadInfographic();
  }, [loadInfographic]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleDownload = () => {
    if (!imageDataUrl) return;
    const a = document.createElement('a');
    a.href = imageDataUrl;
    a.download = `${roleName.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase()}_infographic.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-dark-card border border-gray-700 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-lg font-bold text-dark-text">Position infographic</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-auto flex-1 min-h-0">
          {(loading || generating) && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <div className="w-10 h-10 border-2 border-brand-blue border-t-transparent rounded-full animate-spin mb-3" />
              <span>{generating ? 'Regenerating infographic...' : 'Loading infographic...'}</span>
            </div>
          )}
          {error && !loading && !generating && (
            <div className="py-4 text-red-400 text-sm">
              {error}
              <button
                type="button"
                onClick={loadInfographic}
                className="ml-2 text-brand-blue hover:underline"
              >
                Retry
              </button>
            </div>
          )}
          {imageDataUrl && !loading && !generating && (
            <div className="space-y-3">
              <img
                src={imageDataUrl}
                alt={`Infographic for ${roleName}`}
                className="w-full h-auto rounded-lg border border-gray-700"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleRegenerate}
                  className="px-3 py-1.5 bg-dark-bg border border-dark-border hover:border-indigo-500 text-gray-300 hover:text-white rounded-lg text-sm transition-colors"
                >
                  Regenerate
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="px-3 py-1.5 bg-brand-blue text-white rounded-lg hover:opacity-90 text-sm"
                >
                  Download PNG
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfographicModal;
